<script setup>
import { useTemplateRef, computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useContactFilterContext } from './contactProvider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConditionRow from './ConditionRow.vue';

const props = defineProps({
  isSegmentView: { type: Boolean, default: false },
  segmentName: { type: String, default: '' },
});

const emit = defineEmits([
  'applyFilter',
  'updateSegment',
  'close',
  'clearFilters',
]);
const { filterTypes } = useContactFilterContext();

const filters = defineModel({
  type: Array,
  default: [],
});
const segmentNameLocal = ref(props.segmentName);

const DEFAULT_FILTER = {
  attributeKey: 'name',
  filterOperator: 'equal_to',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const resetFilter = () => {
  emit('clearFilters');
  filters.value = [{ ...DEFAULT_FILTER }];
};

const open = async () => {
  dialogRef.value?.open();
  // The parent seeds the rows in the same tick it calls open, so the model only
  // lands here after the next render.
  await nextTick();
  if (!filters.value.length) filters.value = [{ ...DEFAULT_FILTER }];
  // The dialog stays mounted between openings, so the name has to be re-read.
  segmentNameLocal.value = props.segmentName;
};

const close = () => dialogRef.value?.close();

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedSegment = () => {
  if (isConditionsValid()) {
    emit('updateSegment', filters.value, segmentNameLocal.value);
    close();
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) return;

  store.dispatch(
    'contacts/setContactFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  emit('applyFilter', filters.value);
  useTrack(CONTACTS_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
  close();
}

const onConfirm = () => {
  if (props.isSegmentView) {
    updateSavedSegment();
    return;
  }
  validateAndSubmit();
};

const filterModalHeaderTitle = computed(() => {
  return !props.isSegmentView
    ? t('CONTACTS_LAYOUT.FILTER.TITLE')
    : t('CONTACTS_LAYOUT.FILTER.EDIT_SEGMENT');
});

const submitButtonLabel = computed(() => {
  return props.isSegmentView
    ? t('CONTACTS_LAYOUT.FILTER.BUTTONS.UPDATE_SEGMENT')
    : t('CONTACTS_LAYOUT.FILTER.BUTTONS.APPLY_FILTERS');
});

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    :title="filterModalHeaderTitle"
    @confirm="onConfirm"
    @close="emit('close')"
  >
    <div v-if="props.isSegmentView" class="pb-6 border-b border-n-weak">
      <Input
        v-model="segmentNameLocal"
        :label="$t('CONTACTS_LAYOUT.FILTER.SEGMENT.LABEL')"
        :placeholder="t('CONTACTS_LAYOUT.FILTER.SEGMENT.INPUT_PLACEHOLDER')"
      />
    </div>
    <div class="flex flex-col gap-2">
      <ul class="grid gap-4 list-none">
        <template v-for="(filter, index) in filters" :key="filter.id">
          <ConditionRow
            v-if="index === 0"
            ref="conditionsRef"
            :key="`filter-${filter.attributeKey}-0`"
            v-model:attribute-key="filter.attributeKey"
            v-model:filter-operator="filter.filterOperator"
            v-model:values="filter.values"
            :filter-types="filterTypes"
            :show-query-operator="false"
            @remove="removeFilter(index)"
          />
          <ConditionRow
            v-else
            :key="`filter-${filter.attributeKey}-${index}`"
            ref="conditionsRef"
            v-model:attribute-key="filter.attributeKey"
            v-model:filter-operator="filter.filterOperator"
            v-model:query-operator="filters[index - 1].queryOperator"
            v-model:values="filter.values"
            show-query-operator
            :filter-types="filterTypes"
            @remove="removeFilter(index)"
          />
        </template>
      </ul>
      <Button
        type="button"
        sm
        ghost
        blue
        class="self-start"
        icon="i-lucide-plus"
        @click="addFilter"
      >
        {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.ADD_FILTER') }}
      </Button>
    </div>
    <template #footer>
      <div class="flex gap-2 justify-end">
        <Button
          type="button"
          sm
          faded
          slate
          class="whitespace-nowrap"
          @click="resetFilter"
        >
          {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.CLEAR_FILTERS') }}
        </Button>
        <Button
          type="submit"
          sm
          solid
          blue
          class="whitespace-nowrap"
          :disabled="isSegmentView && !segmentNameLocal"
        >
          {{ submitButtonLabel }}
        </Button>
      </div>
    </template>
  </Dialog>
</template>
